import { createFileRoute, useLocation } from '@tanstack/react-router';
import { useAtomRefresh, useAtomValue } from '@effect/atom-react';
import { Cause, Duration, Effect, Option, Predicate, Schema } from 'effect';
import { AsyncResult, Atom } from 'effect/reactivity';
import { PUBLIC_TOOL_PDF_MAX_PAGES } from '@listening/api-contract';
import { ApiClient } from '@listening/api-contract/api-client-tag';
import { SchemaUtils } from '@listening/api-contract/schema-utils';
import { TooltipProvider } from '@listening/frontend/components/radix/tooltip';
import { cn } from '@listening/utils/react-helpers';
import { useAudioPrefsStore } from '@listening/frontend/stores/audio-preferences-store';
import { apiQuery, pollEvery } from '@listening/frontend/query/api-runtime';
import { audioDetailAtom } from '@listening/frontend/query/audio-detail';
import { Check, Copy } from 'iconoir-react';
import { useEffect, useRef } from 'react';
import { ErrorComponent } from '@/components/shared-ui/error-component';
import { LoadingComponent } from '@/components/shared-ui/loading-component';
import { PlayBar } from '@/components/start/play-bar/play-bar';
import { ReadAlongPage } from '@/components/start/read-along/read-along-page';
import { ReadAlongSurfaceProvider } from '@/components/start/read-along/read-along-store';
import { voicesAtom } from '@listening/frontend/query/voices';
import {
  AudioChunkPlayerProvider,
  useAudioChunkPlayer,
  useAudioChunkSelector,
} from '@/lib/audio-chunk-player';
import type { PublicToolGenerationPrefs } from '@/lib/public-tool-generation-prefs';
import {
  parsePublicToolGenerationPrefs,
  useApplyPublicToolGenerationPrefs,
} from '@/lib/public-tool-generation-prefs';
import type { PublicToolReadAlongPreview } from '@/lib/public-tool-job';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { optionalCatching } from '@/lib/search-params';
import * as m from '@/paraglide/messages';
import { localizeHref } from '@/paraglide/runtime';
import { seo } from '@/seo/seo';
import { useCopyText } from '@/lib/clipboard';

/**
 * `?autoplay=1` from the share links, or a hand-typed `=true`, which the
 * router decodes to a boolean before validation.
 */
const truthyFlag = Schema.Literals(['1', 'true', 1, true]);

const publicToolReadAlongSearchSchema = Schema.Struct({
  speed: optionalCatching(SchemaUtils.NumberOrNumberFromString),
  voice: optionalCatching(Schema.String),
  autoscroll: optionalCatching(truthyFlag),
  autoplay: optionalCatching(truthyFlag),
});

export const Route = createFileRoute(
  '/_marketing/public-tools/readalong/$jobId',
)({
  beforeLoad: assertPublicToolsEnabled,
  validateSearch: Schema.toStandardSchemaV1(publicToolReadAlongSearchSchema),
  head: () => ({
    meta: [
      ...seo({
        title: 'Audio read-along | Listening',
        description:
          'Listen to your converted audio with synced text and playback controls.',
      }),
    ],
  }),
  component: PublicToolReadAlongRoute,
});

function PublicToolReadAlongRoute() {
  const { jobId } = Route.useParams();
  const search = Route.useSearch();
  const generationPrefs = parsePublicToolGenerationPrefs(search);
  const jobResult = usePublicToolJob(jobId);
  // Converters navigate here immediately after creating the job, carrying the
  // source content so the page is never a bare spinner. Absent on refresh or
  // shared links — those fall back to the shimmer skeleton.
  const preview = useLocation({
    select: (l) => l.state.publicToolPreview,
  });

  if (AsyncResult.isInitial(jobResult)) {
    return <PublicToolPendingPreview preview={preview} status={undefined} />;
  }

  const job = AsyncResult.isSuccess(jobResult) ? jobResult.value : null;
  const result = job?.status === 'completed' ? job.result : null;
  // While the job is still processing, the partial marker means the audio
  // record is materialized and the reader can synthesize chunks on demand —
  // open immediately instead of waiting for the full synthesis. A FAILED job
  // with a partial marker is still playable the same way (synthesis died after
  // materialization), so keep the reader instead of an error screen.
  const audioId = result?.audio_id ?? job?.partial?.audio_id;

  if (!audioId) {
    if (AsyncResult.isFailure(jobResult) || job?.status === 'failed') {
      return (
        <div className='flex h-dvh items-center justify-center bg-white dark:bg-[#0a0e1a]'>
          <ErrorComponent
            header={
              job?.status === 'failed'
                ? (job.error_message ?? 'This read-along failed.')
                : 'Could not load this read-along.'
            }
            onRefresh={reloadCurrentPage}
            promptRefresh
          />
        </div>
      );
    }
    if (job?.status === 'completed') {
      return (
        <div className='flex h-dvh items-center justify-center bg-white dark:bg-[#0a0e1a]'>
          <ErrorComponent
            header='Could not load this read-along.'
            onRefresh={reloadCurrentPage}
            promptRefresh
          />
        </div>
      );
    }
    return <PublicToolPendingPreview preview={preview} status={job?.status} />;
  }

  const effectiveGenerationPrefs: PublicToolGenerationPrefs = {
    ...generationPrefs,
    voice:
      generationPrefs.voice ??
      job?.voice ??
      (result && result.tool !== 'pdf-to-audio' ? result.tts.voice : undefined),
  };

  return (
    <PublicToolReadAlongPlayer
      audioId={audioId}
      generationPrefs={effectiveGenerationPrefs}
      jobId={jobId}
      jobSettled={job?.status === 'completed' || job?.status === 'failed'}
      tool={job?.tool}
    />
  );
}

function PublicToolReadAlongPlayer({
  audioId,
  generationPrefs,
  jobId,
  jobSettled,
  tool,
}: {
  audioId: string;
  generationPrefs: PublicToolGenerationPrefs;
  jobId: string;
  jobSettled: boolean;
  tool: PublicToolReadAlongPreview['tool'] | undefined;
}) {
  useApplyPublicToolGenerationPrefs(generationPrefs);
  const audioDetailQuery = usePublicToolAudioDetailedView(audioId, jobSettled);
  const truncation = usePublicToolTruncation(jobId);
  const docFullscreen = useAudioPrefsStore((s) => s.docFullscreen);

  if (
    audioDetailQuery.isPending ||
    audioDetailQuery.rechunking ||
    // While the job is still synthesizing, any detail error is treated as
    // transient (the audio record is seconds old, and rolling deploys can
    // briefly reject processing jobs) — keep loading; the query refetches
    // until the job settles. A terminal error screen here would strand users
    // who arrived from the early "read-along ready" redirect.
    (audioDetailQuery.isError && !jobSettled)
  ) {
    return (
      <div className='flex h-dvh items-center justify-center bg-white dark:bg-[#0a0e1a]'>
        <LoadingComponent />
      </div>
    );
  }

  if (audioDetailQuery.isError) {
    return (
      <div className='flex h-dvh items-center justify-center bg-white dark:bg-[#0a0e1a]'>
        <ErrorComponent
          header='Could not load this read-along.'
          onRefresh={reloadCurrentPage}
          promptRefresh
        />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <ReadAlongSurfaceProvider
        surface='public-tool'
        truncation={truncation ?? undefined}
        initialViewer={tool === 'pdf-to-audio' ? 'pdf' : undefined}
      >
        <AudioChunkPlayerProvider
          initialAudioId={audioId}
          initialLookupAudioId={audioId}
        >
          <PublicToolInitialChunkLoader
            audioId={audioId}
            generationPrefs={generationPrefs}
          />
          <div className='flex h-dvh min-h-0 flex-col overflow-hidden bg-white dark:bg-[#0a0e1a]'>
            <div className='flex min-h-0 flex-1 flex-col'>
              <main className='min-h-0 flex-1 overflow-hidden'>
                <ReadAlongPage slug={audioId} />
              </main>
              <div className={cn('dark', docFullscreen && 'max-sm:hidden')}>
                <PlayBar className='shadow-none' />
              </div>
              <PublicToolExpiryCallout jobId={jobId} />
            </div>
          </div>
        </AudioChunkPlayerProvider>
      </ReadAlongSurfaceProvider>
    </TooltipProvider>
  );
}

const PREVIEW_SHIMMER_WIDTHS = [
  '92%',
  '100%',
  '96%',
  '88%',
  '99%',
  '94%',
  '72%',
];

/**
 * Shown between arrival and the job's "read-along ready" marker. Converters
 * navigate here immediately after creating the job, so this is the page users
 * watch for the first ~2-5s: the typed text (when available) or a skeleton,
 * with an honest stage label instead of a bare spinner.
 */
function PublicToolPendingPreview({
  preview,
  status,
}: {
  preview: PublicToolReadAlongPreview | undefined;
  // Full status union: TS can't see that settled states returned earlier.
  status: 'pending' | 'processing' | 'completed' | 'failed' | undefined;
}) {
  const label =
    status === undefined || status === 'pending'
      ? 'Starting your conversion…'
      : preview?.tool === 'web-to-audio'
        ? 'Reading the article…'
        : preview?.tool === 'pdf-to-audio'
          ? 'Reading your PDF…'
          : 'Preparing your audio…';

  const paragraphs = preview?.text
    ? preview.text
        .split(/\n+/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean)
        .slice(0, 40)
    : null;

  return (
    <div className='flex h-dvh flex-col overflow-hidden bg-white dark:bg-[#0a0e1a]'>
      <div className='mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col gap-6 overflow-y-auto px-6 py-12'>
        <div
          aria-live='polite'
          className='flex items-center gap-3 text-sm font-medium text-slate-500 dark:text-slate-400'
        >
          <span
            aria-hidden='true'
            className='inline-block size-2 animate-pulse rounded-full bg-royal-50'
          />
          {label}
        </div>
        {paragraphs ? (
          <>
            {preview?.title ? (
              <h1 className='text-2xl font-semibold text-slate-900 dark:text-white'>
                {preview.title}
              </h1>
            ) : null}
            <div className='space-y-4 text-[17px] leading-relaxed text-slate-700/80 dark:text-slate-200/80'>
              {paragraphs.map((paragraph, index) => (
                <p key={`${index.toString()}-${paragraph.length.toString()}`}>
                  {paragraph}
                </p>
              ))}
            </div>
          </>
        ) : (
          <div aria-hidden='true' className='space-y-3 pt-2'>
            {PREVIEW_SHIMMER_WIDTHS.map((width, index) => (
              <div
                className='h-4 animate-pulse rounded bg-slate-200/80 dark:bg-slate-700/50'
                key={`${index.toString()}-${width}`}
                style={{ width }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function reloadCurrentPage() {
  window.location.reload();
}

function usePublicToolAudioDetailedView(audioId: string, jobSettled: boolean) {
  const detailAtom = audioDetailAtom(audioId);
  const result = useAtomValue(detailAtom);
  const refresh = useAtomRefresh(detailAtom);
  const failed = AsyncResult.isFailure(result);
  const rechunking =
    failed &&
    Option.exists(
      Cause.findErrorOption(result.cause),
      Predicate.isTagged('AudioRechunkingError'),
    );
  // Retry transient detail errors while the job is still synthesizing (see the
  // loading branch in PublicToolReadAlongPlayer).
  const retryDelayMs =
    !failed || result.waiting
      ? undefined
      : rechunking
        ? 5_000
        : jobSettled
          ? undefined
          : 2_500;
  useEffect(() => {
    if (retryDelayMs === undefined) return;
    const timer = setTimeout(refresh, retryDelayMs);
    return () => clearTimeout(timer);
  }, [refresh, retryDelayMs]);
  return {
    isPending: AsyncResult.isInitial(result),
    isError: failed,
    rechunking,
  };
}

// Seeding `initialAudioId` skips the auto-load in `useLoadAudioFromSlug`, but it
// also leaves the player without a chunk, making the play button a no-op until
// the user clicks a word or the seek bar. Pre-load the first chunk so play works
// immediately. When the converter requested autoplay (the user just clicked
// "Generate"), attempt playback right away — best-effort, since the browser may
// have expired the user activation during the wait; a block leaves it paused.
function PublicToolInitialChunkLoader({
  audioId,
  generationPrefs,
}: {
  audioId: string;
  generationPrefs: PublicToolGenerationPrefs;
}) {
  const { loadAudio } = useAudioChunkPlayer();
  const ttsId = useAudioChunkSelector((s) => s.ttsId);
  const docPackChunk = useAudioChunkSelector((s) => s.docPackChunk);
  const didLoadRef = useRef(false);
  const voicesPending = useAtomValue(voicesAtom, AsyncResult.isInitial);

  useEffect(() => {
    if (ttsId || docPackChunk || didLoadRef.current) return;
    if (generationPrefs.voice && voicesPending) return;
    didLoadRef.current = true;
    loadAudio(audioId, undefined, undefined, {
      loadPos: { posMs: 0 },
      autoPlay: generationPrefs.autoplay === true,
    });
  }, [
    audioId,
    generationPrefs.autoplay,
    generationPrefs.voice,
    docPackChunk,
    loadAudio,
    ttsId,
    voicesPending,
  ]);

  return null;
}

const publicToolJobAtom = Atom.family((jobId: string) =>
  apiQuery(
    Effect.flatMap(ApiClient, (api) =>
      api.PublicTools.GetJob({ params: { jobId } }),
    ),
    { staleTime: Duration.minutes(1) },
  ).pipe(
    // Poll while the job is still synthesizing so the page picks up the
    // partial "read-along ready" marker and, later, the completed result.
    // Pre-marker the user is staring at the preview, so poll fast; once the
    // reader is up only the completion metadata is outstanding.
    pollEvery((job) =>
      Option.match(job, {
        onNone: () => false,
        onSome: (data) =>
          data.status === 'pending'
            ? 750
            : data.status === 'processing'
              ? data.partial?.audio_id
                ? 2_000
                : 750
              : false,
      }),
    ),
  ),
);

const usePublicToolJob = (jobId: string) =>
  useAtomValue(publicToolJobAtom(jobId));

/** The time, ticking every minute while something shows it. */
const expiryClockAtom = Atom.make((get) => {
  const timer = setInterval(() => get.setSelf(Date.now()), 60_000);
  get.addFinalizer(() => clearInterval(timer));
  return Date.now();
});

function usePublicToolExpiry(jobId: string) {
  const job = AsyncResult.getOrElse(usePublicToolJob(jobId), () => undefined);
  const now = useAtomValue(expiryClockAtom);

  const result = job?.result;
  const expiresAtMs =
    (result && result.tool !== 'pdf-to-audio'
      ? result.read_along?.expires_at_ms
      : undefined) ?? job?.partial?.expires_at_ms;

  if (!expiresAtMs) return null;
  return Math.max(0, expiresAtMs - now);
}

/** The pages read aloud and the PDF's page count, when some pages were not. */
function usePublicToolTruncation(jobId: string) {
  const job = AsyncResult.getOrElse(usePublicToolJob(jobId), () => undefined);
  const pages =
    job?.result?.tool === 'pdf-to-audio' ? job.result : job?.partial;
  const totalPageCount = pages?.source_page_count;
  if (totalPageCount === undefined) return null;
  const convertedPageCount =
    pages?.converted_page_count ??
    Math.min(totalPageCount, PUBLIC_TOOL_PDF_MAX_PAGES);
  return convertedPageCount < totalPageCount
    ? { convertedPageCount, totalPageCount }
    : null;
}

function formatRemaining(remainingMs: number) {
  const totalMinutes = Math.max(0, Math.ceil(remainingMs / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes.toString()}m`;
  if (minutes === 0) return `${hours.toString()}h`;
  return `${hours.toString()}h ${minutes.toString()}m`;
}

function PublicToolExpiryCallout({ jobId }: { jobId: string }) {
  const remainingMs = usePublicToolExpiry(jobId);
  const truncation = usePublicToolTruncation(jobId);
  const docFullscreen = useAudioPrefsStore((s) => s.docFullscreen);
  const { copy, copied: didCopyUrl } = useCopyText();
  if (remainingMs === null) return null;

  const remaining = formatRemaining(remainingMs);
  const copyUrl = () => {
    void copy(window.location.href);
  };

  return (
    <>
      <div className='fixed right-4 bottom-[88px] z-30 hidden w-[360px] rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_16px_48px_rgba(15,23,42,0.18)] sm:block dark:border-white/10 dark:bg-slate-900'>
        <p className='text-sm font-semibold text-slate-950 dark:text-white'>
          This read-along expires in {remaining}
        </p>
        <p className='mt-1 text-sm leading-5 text-slate-600 dark:text-slate-300'>
          {truncation === null
            ? 'Sign up to keep this document active forever in your Listening library.'
            : m.read_along_free_preview_pages({
                converted: truncation.convertedPageCount,
                total: truncation.totalPageCount,
              })}
        </p>
        <div className='mt-3 flex items-center gap-2'>
          <a
            className='inline-flex h-9 flex-1 items-center justify-center rounded-full bg-royal-50 px-4 text-center text-sm font-semibold text-white transition hover:bg-royal-50/90'
            href={localizeHref('/app/continue')}
          >
            Sign up
          </a>
          <div className='relative'>
            {didCopyUrl && (
              <div className='absolute bottom-full left-1/2 mb-2 -translate-x-1/2 rounded-md bg-green-600 px-2 py-1 text-xs font-medium whitespace-nowrap text-white shadow-lg dark:bg-green-500 dark:text-white'>
                URL Copied
              </div>
            )}
            <button
              className='inline-flex size-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700'
              type='button'
              aria-label='Copy read-along URL'
              onClick={copyUrl}
            >
              {didCopyUrl ? (
                <Check className='size-4 text-green-600 dark:text-green-400' />
              ) : (
                <Copy className='size-4' />
              )}
            </button>
          </div>
        </div>
      </div>

      {!docFullscreen && (
        <div className='fixed inset-x-3 top-[76px] z-30 rounded-full border border-slate-200 bg-white/95 px-4 py-2 text-center text-xs font-medium text-slate-800 shadow-sm sm:hidden dark:border-white/10 dark:bg-slate-900/95 dark:text-slate-100'>
          {truncation === null
            ? `Expires in ${remaining}.`
            : m.read_along_first_pages({
                converted: truncation.convertedPageCount,
                total: truncation.totalPageCount,
              })}{' '}
          <a
            className='font-semibold text-royal-50'
            href={localizeHref('/app/continue')}
          >
            {truncation === null
              ? 'Sign up to keep it forever.'
              : 'Sign up for the full PDF.'}
          </a>
        </div>
      )}
    </>
  );
}
